import { ReactNode, useEffect, useMemo, useState } from "react";
import axios from "../../../../services/axios";
import { useFilter } from "../../../../hooks/useFilter";
import { FooterEditOnboarding } from "../../../EditOnboarding/Footer";
import { ButtonOnlyTitle } from "../../../Buttons/ButtonOnlyTitle/Index";
import { DropdownV2, InputV2, OptionV2 } from "../../../FormControlsV2";
import { AlertModal } from "../../../Modal/Alert/Index";
import { TitleModal } from "../../../Modal/Contents/TitleModal/Index";
import { TextModal } from "../../../Modal/Contents/TextModal/Index";
import { ReactComponent as CheckIcon } from "../../../../assets/svg/icons/CheckFull.svg";
import "./styles.css";
import { useTranslation } from "react-i18next";

type CatalogProduct = {
  id: number;
  trade_name: string;
  manufacturer?: string | null;
  classification?: string | null;
  packaging_type: ProductPackagingType;
  bag_weight_kg: number | null;
};

type StockProduct = {
  product_id: number;
  quantity: string;
  quantity_kg?: number;
  bag_quantity_equivalent?: number | null;
  packaging_type?: ProductPackagingType;
  bag_weight_kg?: number | null;
};

type PastureStock = {
  micro_area_id: number;
  products: StockProduct[];
};

type Inventory = {
  central_storage: StockProduct[];
  pasture_storage: PastureStock[];
};

type ApiInventory = {
  central_storage?: ApiStockProduct[];
  pasture_storage?: Array<{
    micro_area_id: number;
    products?: ApiStockProduct[];
  }>;
};

type ApiStockProduct = {
  product_id: number;
  quantity_kg: number;
  bag_quantity_equivalent?: number | null;
  packaging_type?: ProductPackagingType;
  bag_weight_kg?: number | null;
};

type MicroArea = {
  id: number;
  initials?: string;
  area?: { initials?: string };
  farm?: { initials?: string };
};

const emptyInventory: Inventory = { central_storage: [], pasture_storage: [] };

function normalizeInventory(data: ApiInventory | undefined, products: CatalogProduct[]): Inventory {
  const normalizeProduct = (item: ApiStockProduct): StockProduct => {
    const product = products.find((product) => product.id === Number(item.product_id));
    const packagingType = product?.packaging_type ?? item.packaging_type;
    const bagWeight = product?.bag_weight_kg ?? item.bag_weight_kg ?? null;
    const quantityKg = Number(item.quantity_kg ?? 0);
    return {
      product_id: Number(item.product_id),
      packaging_type: packagingType,
      bag_weight_kg: bagWeight,
      quantity_kg: quantityKg,
      bag_quantity_equivalent: item.bag_quantity_equivalent ?? null,
      quantity: String(packagingType === "BAGGED"
        ? item.bag_quantity_equivalent ?? (bagWeight && bagWeight > 0 ? quantityKg / bagWeight : "")
        : quantityKg),
    };
  };
  return removeZeroQuantityProducts({
    central_storage: (data?.central_storage || []).map(normalizeProduct),
    pasture_storage: (data?.pasture_storage || []).map((storage) => ({
      micro_area_id: Number(storage.micro_area_id),
      products: (storage.products || []).map(normalizeProduct),
    })),
  });
}

function removeZeroQuantityProducts(inventory: Inventory): Inventory {
  return {
    central_storage: inventory.central_storage.filter(
      (item) => item.quantity === "" || Number(item.quantity) !== 0,
    ),
    pasture_storage: inventory.pasture_storage
      .map((storage) => ({
        ...storage,
        products: storage.products.filter(
          (item) => item.quantity === "" || Number(item.quantity) !== 0,
        ),
      }))
      .filter((storage) => storage.products.length > 0),
  };
}

function getProductPackagingType(item: StockProduct, products: CatalogProduct[]): ProductPackagingType | undefined {
  return products.find((product) => product.id === item.product_id)?.packaging_type ?? item.packaging_type;
}

type StockManagementProps = {
  selectedFarm: OptionV2<number> | null;
  onDirtyChange: (hasChanges: boolean) => void;
  mobileHeaderAction?: ReactNode;
};

export function StockManagement({
  selectedFarm,
  onDirtyChange,
  mobileHeaderAction,
}: StockManagementProps) {
  const { t, i18n } = useTranslation();
  const { setSystemManager } = useFilter();
  const [products, setProducts] = useState<CatalogProduct[]>([]);
  const [microAreas, setMicroAreas] = useState<MicroArea[]>([]);
  const [inventory, setInventory] = useState<Inventory>(emptyInventory);
  const [initialInventory, setInitialInventory] =
    useState<Inventory>(emptyInventory);
  const [enabledPastures, setEnabledPastures] = useState<number[]>([]);
  const [pendingPasture, setPendingPasture] = useState<OptionV2<number> | null>(
    null,
  );
  const [pendingProducts, setPendingProducts] = useState<
    Record<string, OptionV2<number> | null>
  >({});
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [saving, setSaving] = useState(false);
  const [successVisible, setSuccessVisible] = useState(false);

  const productOptions = useMemo<OptionV2<number>[]>(
    () =>
      products.map((product) => ({
        label: product.trade_name,
        value: product.id,
        info: product,
      })),
    [products],
  );

  useEffect(() => {
    if (!selectedFarm) {
      setLoading(false);
      setLoadFailed(false);
      setShowValidation(false);
      setInventory(emptyInventory);
      setInitialInventory(emptyInventory);
      setMicroAreas([]);
      return;
    }

    let active = true;
    setLoading(true);
    setLoadFailed(false);
    setShowValidation(false);
    setInventory(emptyInventory);
    setInitialInventory(emptyInventory);
    setPendingPasture(null);
    setPendingProducts({});
    Promise.all([
      axios.get<CatalogProduct[]>("/products/all"),
      axios.get<ApiInventory>(`/farms/${selectedFarm.value}/inventory`),
      axios.get<MicroArea[]>("/micro-areas", {
        params: { farm_id: selectedFarm.value },
      }),
    ])
      .then(([catalogResponse, inventoryResponse, microAreaResponse]) => {
        if (!active) return;
        const catalog = catalogResponse.data || [];
        const normalized = normalizeInventory(inventoryResponse.data, catalog);
        setProducts(catalog);
        setInventory(normalized);
        setInitialInventory(normalized);
        setMicroAreas(
          (microAreaResponse.data || []).sort((a, b) =>
            getMicroAreaLabel(a).localeCompare(getMicroAreaLabel(b)),
          ),
        );
        setEnabledPastures(
          normalized.pasture_storage
            .filter((storage) => storage.products.length > 0)
            .map((storage) => storage.micro_area_id),
        );
      })
      .catch(() => {
        if (active) setLoadFailed(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [selectedFarm]);

  useEffect(() => {
    onDirtyChange(
      JSON.stringify(inventory) !== JSON.stringify(initialInventory),
    );
  }, [initialInventory, inventory, onDirtyChange]);

  const allPastures = useMemo(() => {
    const byId = new Map<number, MicroArea>();
    microAreas.forEach((microArea) =>
      byId.set(Number(microArea.id), microArea),
    );
    inventory.pasture_storage.forEach((storage) => {
      if (!byId.has(storage.micro_area_id))
        byId.set(storage.micro_area_id, { id: storage.micro_area_id });
    });
    return Array.from(byId.values()).sort((a, b) =>
      getMicroAreaLabel(a).localeCompare(getMicroAreaLabel(b)),
    );
  }, [inventory.pasture_storage, microAreas]);

  const displayedPastures = useMemo(
    () =>
      allPastures.filter((microArea) => {
        const microAreaId = Number(microArea.id);
        const storage = inventory.pasture_storage.find(
          (item) => item.micro_area_id === microAreaId,
        );
        return (
          enabledPastures.includes(microAreaId) ||
          Boolean(storage?.products.length)
        );
      }),
    [allPastures, enabledPastures, inventory.pasture_storage],
  );

  const pastureOptions = useMemo<OptionV2<number>[]>(
    () =>
      allPastures
        .filter(
          (microArea) =>
            !displayedPastures.some(
              (displayed) => Number(displayed.id) === Number(microArea.id),
            ),
        )
        .map((microArea) => ({
          label: getMicroAreaLabel(microArea),
          value: Number(microArea.id),
          info: microArea,
        })),
    [allPastures, displayedPastures],
  );

  function addPastureStorage() {
    if (!pendingPasture) return;
    setEnabledPastures((current) =>
      current.includes(pendingPasture.value)
        ? current
        : [...current, pendingPasture.value],
    );
    setPendingPasture(null);
  }

  function updateCentralQuantity(productId: number, quantity: string) {
    setInventory((current) => ({
      ...current,
      central_storage: current.central_storage.map((item) =>
        item.product_id === productId ? { ...item, quantity } : item,
      ),
    }));
  }

  function updatePastureQuantity(
    microAreaId: number,
    productId: number,
    quantity: string,
  ) {
    setInventory((current) => ({
      ...current,
      pasture_storage: current.pasture_storage.map((storage) =>
        storage.micro_area_id === microAreaId
          ? {
              ...storage,
              products: storage.products.map((item) =>
                item.product_id === productId ? { ...item, quantity } : item,
              ),
            }
          : storage,
      ),
    }));
  }

  function addProduct(storageKey: string, microAreaId?: number) {
    const selectedProduct = pendingProducts[storageKey];
    if (!selectedProduct) return;

    const product = products.find((product) => product.id === selectedProduct.value);
    const newStockProduct: StockProduct = {
      product_id: selectedProduct.value,
      quantity: "0",
      packaging_type: product?.packaging_type,
      bag_weight_kg: product?.bag_weight_kg,
    };
    setInventory((current) => {
      if (microAreaId === undefined) {
        if (
          current.central_storage.some(
            (item) => item.product_id === selectedProduct.value,
          )
        )
          return current;
        return {
          ...current,
          central_storage: [
            ...current.central_storage,
            { ...newStockProduct },
          ],
        };
      }

      const existingStorage = current.pasture_storage.find(
        (storage) => storage.micro_area_id === microAreaId,
      );
      if (!existingStorage) {
        return {
          ...current,
          central_storage: current.central_storage.some(
            (item) => item.product_id === selectedProduct.value,
          )
            ? current.central_storage
            : [
                ...current.central_storage,
                { ...newStockProduct },
              ],
          pasture_storage: [
            ...current.pasture_storage,
            {
              micro_area_id: microAreaId,
              products: [{ ...newStockProduct }],
            },
          ],
        };
      }
      if (
        existingStorage.products.some(
          (item) => item.product_id === selectedProduct.value,
        )
      )
        return current;
      return {
        ...current,
        central_storage: current.central_storage.some(
          (item) => item.product_id === selectedProduct.value,
        )
          ? current.central_storage
          : [
              ...current.central_storage,
              { ...newStockProduct },
            ],
        pasture_storage: current.pasture_storage.map((storage) =>
          storage.micro_area_id === microAreaId
            ? {
                ...storage,
                products: [
                  ...storage.products,
                  { ...newStockProduct },
                ],
              }
            : storage,
        ),
      };
    });
    setPendingProducts((current) => ({ ...current, [storageKey]: null }));
  }

  function getAvailableProducts(stockProducts: StockProduct[]) {
    const usedIds = new Set(stockProducts.map((item) => item.product_id));
    return productOptions.filter((option) => !usedIds.has(option.value));
  }

  function getQuantityError(item: StockProduct) {
    const packagingType = getProductPackagingType(item, products);
    if (packagingType !== "BAGGED" && packagingType !== "BULK") {
      return t("stockManagement.packagingUnavailable");
    }
    const weight = products.find((product) => product.id === item.product_id)?.bag_weight_kg ?? item.bag_weight_kg;
    if (packagingType === "BAGGED" && (!weight || !Number.isFinite(Number(weight)) || Number(weight) <= 0)) {
      return t("stockManagement.bagWeightUnavailable");
    }
    if (item.quantity.trim() === "" || !Number.isFinite(Number(item.quantity)) || Number(item.quantity) < 0) {
      return t("stockManagement.invalidQuantity");
    }
    return undefined;
  }

  function validateInventory() {
    return [
      ...inventory.central_storage,
      ...inventory.pasture_storage.flatMap((storage) => storage.products),
    ].every((item) => !getQuantityError(item));
  }

  function saveInventory() {
    if (!selectedFarm || saving || loading || loadFailed) return;
    setShowValidation(true);
    if (!validateInventory()) {
      return;
    }

    setSaving(true);
    const payload = {
      central_storage: inventory.central_storage.map((item) => ({
        product_id: item.product_id,
        ...(getProductPackagingType(item, products) === "BAGGED"
          ? { bag_quantity: Number(item.quantity) }
          : { quantity_kg: Number(item.quantity) }),
      })),
      pasture_storage: inventory.pasture_storage
        .filter((storage) => storage.products.length > 0)
        .map((storage) => ({
          micro_area_id: storage.micro_area_id,
          products: storage.products.map((item) => ({
            product_id: item.product_id,
            ...(getProductPackagingType(item, products) === "BAGGED"
              ? { bag_quantity: Number(item.quantity) }
              : { quantity_kg: Number(item.quantity) }),
          })),
        })),
    };

    axios
      .patch(`/farms/${selectedFarm.value}/inventory`, payload)
      .then(() => {
        const inventoryWithoutZero = removeZeroQuantityProducts(inventory);
        setInventory(inventoryWithoutZero);
        setInitialInventory(inventoryWithoutZero);
        setEnabledPastures(
          inventoryWithoutZero.pasture_storage.map(
            (storage) => storage.micro_area_id,
          ),
        );
        setPendingProducts({});
        setSuccessVisible(true);
      })
      .finally(() => setSaving(false));
  }

  function cancelChanges() {
    setSystemManager(null);
  }

  function renderProducts(stockProducts: StockProduct[], microAreaId?: number) {
    if (!stockProducts.length)
      return (
        <p className="stock-management__empty">
          {t("stockManagement.noProducts")}
        </p>
      );
    return (
      <div className="stock-management__products">
        <div className="stock-management__product-header" aria-hidden="true">
          <span>{t("stockManagement.product")}</span>
          <span>{t("stockManagement.quantity")}</span>
        </div>
        {stockProducts.map((item) => {
          const product = products.find(
            (catalogProduct) => catalogProduct.id === item.product_id,
          );
          const packagingType = getProductPackagingType(item, products);
          const isBagged = packagingType === "BAGGED";
          const bagWeight = product?.bag_weight_kg ?? item.bag_weight_kg;
          const quantityKg = Number(item.quantity) * Number(bagWeight);
          return (
            <div
              className="stock-management__product-row"
              key={item.product_id}
            >
              <div>
                <strong>
                  {product?.trade_name || t("stockManagement.productFallback", { id: item.product_id })}
                </strong>
                {product?.classification && (
                  <small>{product.classification}</small>
                )}
              </div>
              <div className="stock-management__quantity">
                <InputV2
                  name={`quantity-${microAreaId ?? "central"}-${item.product_id}`}
                  type="number"
                  min={0}
                  step="any"
                  error={showValidation ? getQuantityError(item) : undefined}
                  value={item.quantity}
                  onChange={(value) =>
                    microAreaId === undefined
                      ? updateCentralQuantity(item.product_id, value)
                      : updatePastureQuantity(microAreaId, item.product_id, value)
                  }
                  suffix={isBagged ? t("stockManagement.bags") : packagingType === "BULK" ? "kg" : undefined}
                  anchor="end"
                />
                {isBagged && Number(bagWeight) > 0 && item.quantity.trim() !== "" && Number.isFinite(quantityKg) && (
                  <small>{t("stockManagement.kgEquivalent", {
                    value: new Intl.NumberFormat(i18n.resolvedLanguage || i18n.language, { maximumFractionDigits: 6 }).format(quantityKg),
                  })}</small>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  function renderAddProduct(
    storageKey: string,
    stockProducts: StockProduct[],
    microAreaId?: number,
  ) {
    const availableProducts = getAvailableProducts(stockProducts);
    if (!availableProducts.length)
      return (
        <small className="stock-management__all-added">
          {t("stockManagement.allProductsAdded")}
        </small>
      );
    return (
      <div className="stock-management__add-product">
        <div style={{ maxWidth: 350 }}>
          <DropdownV2
            name={`product-${storageKey}`}
            value={pendingProducts[storageKey] || null}
            options={availableProducts}
            placeholder={t("stockManagement.selectProduct")}
            searchable
            menuMount="overlay"
            onChange={(option) =>
              setPendingProducts((current) => ({
                ...current,
                [storageKey]: option,
              }))
            }
          />
        </div>
        <ButtonOnlyTitle
          title={t("stockManagement.add")}
          theme="light"
          disabled={!pendingProducts[storageKey]}
          onClick={() => addProduct(storageKey, microAreaId)}
        />
      </div>
    );
  }

  return (
    <div className="stock-management">
      {mobileHeaderAction && <div className="system-manager__mobile-content-action">{mobileHeaderAction}</div>}
      <div className="stock-management__scroll">
        {loading && (
          <p className="stock-management__status">{t("stockManagement.loading")}</p>
        )}

        {loadFailed && <p className="stock-management__message stock-management__message--error" role="alert">{t("stockManagement.loadFailed")}</p>}

        {!selectedFarm && !loading && (
          <div className="stock-management__placeholder">
            <strong>{t("stockManagement.selectFarm")}</strong>
            <span>{t("stockManagement.selectFarmHelp")}</span>
          </div>
        )}

        {selectedFarm && !loading && !loadFailed && (
          <div className="stock-management__content">
            <section className="stock-management__storage stock-management__storage--central">
              <div className="stock-management__storage-title">
                <div>
                  <small>{t("stockManagement.centralStorage")}</small>
                  <h3>{selectedFarm.label}</h3>
                </div>
                <span>
                  {inventory.central_storage.length}{" "}
                  {t("stockManagement.productCount", { count: inventory.central_storage.length })}
                </span>
              </div>
              {renderProducts(inventory.central_storage)}
              {renderAddProduct("central", inventory.central_storage)}
            </section>

            <div className="stock-management__pastures-heading">
              <h3>{t("stockManagement.pastureStorages")}</h3>
              <span>
                {displayedPastures.length}{" "}
                {t("stockManagement.storageCount", { count: displayedPastures.length })}
              </span>
            </div>

            {!allPastures.length && (
              <p className="stock-management__status">
                {t("stockManagement.noRegisteredPaddocks")}
              </p>
            )}

            <div className="stock-management__pastures">
              {displayedPastures.map((microArea) => {
                const microAreaId = Number(microArea.id);
                const storage = inventory.pasture_storage.find(
                  (item) => item.micro_area_id === microAreaId,
                );
                const stockProducts = storage?.products || [];
                return (
                  <section
                    className="stock-management__storage"
                    key={microAreaId}
                  >
                    <div className="stock-management__storage-title">
                      <div>
                        <small>{t("stockManagement.paddock")}</small>
                        <h3>{getMicroAreaLabel(microArea, t("stockManagement.paddockFallback", { id: microArea.id }))}</h3>
                      </div>
                      <span>
                        {stockProducts.length}{" "}
                        {t("stockManagement.productCount", { count: stockProducts.length })}
                      </span>
                    </div>
                    {renderProducts(stockProducts, microAreaId)}
                    {renderAddProduct(
                      `pasture-${microAreaId}`,
                      stockProducts,
                      microAreaId,
                    )}
                  </section>
                );
              })}

              {pastureOptions.length > 0 && (
                <section className="stock-management__storage stock-management__storage--new">
                  <div className="stock-management__storage-title">
                    <div>
                      <small>{t("stockManagement.paddockWithoutProducts")}</small>
                      <h3>{t("stockManagement.newStorage")}</h3>
                    </div>
                  </div>
                  <div className="stock-management__add-storage">
                    <div style={{ maxWidth: 350 }}>
                      <DropdownV2
                        name="new-pasture-storage"
                        value={pendingPasture}
                        options={pastureOptions}
                        placeholder={t("stockManagement.selectPaddock")}
                        searchable
                        menuMount="overlay"
                        onChange={setPendingPasture}
                      />
                    </div>

                    <ButtonOnlyTitle
                      title={t("stockManagement.add")}
                      theme="light"
                      disabled={!pendingPasture}
                      onClick={addPastureStorage}
                    />
                  </div>
                </section>
              )}
            </div>
          </div>
        )}
      </div>

      <FooterEditOnboarding
        isNew
        undoFunction={cancelChanges}
        saveFunction={saveInventory}
        disabledSubmit={saving}
        disabledSave={!selectedFarm || loading || loadFailed}
      />

      <AlertModal
        visible={successVisible}
        contentStyle={{
          width: 440,
          height: 240,
          borderRadius: 20,
          padding: 30,
        }}
      >
        <section className="stock-management__success-modal">
          <CheckIcon width={42} height={42} />
          <div>
            <TitleModal>{t("stockManagement.completed")}</TitleModal>
            <TextModal>{t("stockManagement.saved")}</TextModal>
          </div>
          <ButtonOnlyTitle
            title={t("stockManagement.close")}
            theme="info"
            onClick={() => setSuccessVisible(false)}
          />
        </section>
      </AlertModal>
    </div>
  );
}

function getMicroAreaLabel(microArea: MicroArea, fallback = `#${microArea.id}`) {
  const prefix = [microArea.farm?.initials, microArea.area?.initials]
    .filter(Boolean)
    .join("-");
  const initials = microArea.initials || fallback;
  return prefix ? `${prefix}-${initials}` : initials;
}
